/**
 * Stats Service for V2 Services
 */

import { FirestoreService } from '../db/FirestoreService';
import { Stats, DashboardStats, GlobalStats, RevenueStats } from '../models/Stats.model';

export class StatsService {
  private firestore: FirestoreService;
  private readonly COLLECTION = 'stats';
  private readonly STATS_DOC_ID = 'global';
  private readonly DASHBOARD_DOC_ID = 'dashboard';

  constructor() {
    this.firestore = FirestoreService.getInstance();
  }

  async getStats(): Promise<Stats> {
    const doc = await this.firestore.getDocument(this.COLLECTION, this.STATS_DOC_ID);
    return doc ? Stats.fromFirestore(doc) : new Stats();
  }

  async updateDashboardStats(updates: Partial<DashboardStats>): Promise<void> {
    const statsData = {
      dashboard: {
        ...updates,
        lastUpdated: new Date()
      }
    };
    await this.firestore.updateDocument(this.COLLECTION, this.STATS_DOC_ID, statsData);
  }

  async updateGlobalStats(updates: Partial<GlobalStats>): Promise<void> {
    const statsData = {
      global: {
        ...updates,
        lastUpdated: new Date()
      }
    };
    await this.firestore.updateDocument(this.COLLECTION, this.STATS_DOC_ID, statsData);
  }

  async updateRevenueStats(updates: Partial<RevenueStats>): Promise<void> {
    const statsData = {
      revenue: {
        ...updates,
        lastUpdated: new Date()
      }
    };
    await this.firestore.updateDocument(this.COLLECTION, this.STATS_DOC_ID, statsData);
  }

  async incrementDashboardStat(field: keyof DashboardStats, value: number = 1): Promise<void> {
    const currentStats = await this.getStats();
    const currentValue = (currentStats.dashboard as any)[field] || 0;

    await this.updateDashboardStats({
      [field]: currentValue + value
    } as Partial<DashboardStats>);
  }

  async incrementGlobalStat(field: keyof GlobalStats, value: number = 1): Promise<void> {
    const currentStats = await this.getStats();
    const currentValue = (currentStats.global as any)[field] || 0;

    await this.updateGlobalStats({
      [field]: currentValue + value
    } as Partial<GlobalStats>);
  }

  async addRevenue(amount: number, date: Date = new Date()): Promise<void> {
    const currentStats = await this.getStats();
    const dateStr = date.toISOString().split('T')[0];
    const weekStr = this.getWeekString(date);
    const monthStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    const yearStr = String(date.getFullYear());

    const updatedRevenue: RevenueStats = {
      daily: {
        ...currentStats.revenue.daily,
        [dateStr]: (currentStats.revenue.daily[dateStr] || 0) + amount
      },
      weekly: {
        ...currentStats.revenue.weekly,
        [weekStr]: (currentStats.revenue.weekly[weekStr] || 0) + amount
      },
      monthly: {
        ...currentStats.revenue.monthly,
        [monthStr]: (currentStats.revenue.monthly[monthStr] || 0) + amount
      },
      yearly: {
        ...currentStats.revenue.yearly,
        [yearStr]: (currentStats.revenue.yearly[yearStr] || 0) + amount
      },
      total: currentStats.revenue.total + amount
    };

    await this.updateRevenueStats(updatedRevenue);
  }

  async calculateDashboardStats(): Promise<DashboardStats> {
    const bookingService = (await import('./BookingService')).BookingService;
    const userService = (await import('./UserService')).UserService;
    const robotService = (await import('./RobotService')).RobotService;

    const bookings = new bookingService();
    const users = new userService();
    const robots = new robotService();

    const [
      allBookings,
      allUsers,
      allRobots,
      bookingStats
    ] = await Promise.all([
      bookings.getAllBookings(),
      users.getAllUsers(),
      robots.getAllRobots(),
      bookings.getBookingStats()
    ]);

    const activeRobots = allRobots.filter(robot => robot.status === 'available' || robot.status === 'cleaning').length;

    const dashboardStats: DashboardStats = {
      totalUsers: allUsers.length,
      totalBookings: bookingStats.total,
      totalRevenue: bookingStats.revenue,
      activeBookings: bookingStats.active,
      completedBookings: bookingStats.completed,
      cancelledBookings: bookingStats.cancelled,
      averageRating: this.calculateAverageRating(allBookings),
      totalRobots: allRobots.length,
      activeRobots,
      lastUpdated: new Date()
    };

    await this.updateDashboardStats(dashboardStats);
    return dashboardStats;
  }

  async getRevenueByPeriod(period: 'daily' | 'weekly' | 'monthly' | 'yearly'): Promise<Record<string, number>> {
    const stats = await this.getStats();
    return stats.revenue[period];
  }

  async getTopRevenueDay(): Promise<{ date: string; amount: number } | null> {
    const dailyRevenue = await this.getRevenueByPeriod('daily');
    let topDay = null;
    let maxAmount = 0;

    for (const [date, amount] of Object.entries(dailyRevenue)) {
      if (amount > maxAmount) {
        maxAmount = amount;
        topDay = { date, amount };
      }
    }

    return topDay;
  }

  async resetStats(): Promise<void> {
    const freshStats = new Stats();
    await this.firestore.setDocument(this.COLLECTION, this.STATS_DOC_ID, freshStats.toFirestore());
  }

  private calculateAverageRating(bookings: any[]): number {
    const ratedBookings = bookings.filter(booking => booking.metadata?.rating);
    if (ratedBookings.length === 0) return 0;

    const totalRating = ratedBookings.reduce((sum, booking) => sum + booking.metadata.rating, 0);
    return Math.round((totalRating / ratedBookings.length) * 10) / 10;
  }

  private getWeekString(date: Date): string {
    const startOfWeek = new Date(date);
    const day = startOfWeek.getDay();
    const diff = startOfWeek.getDate() - day;
    startOfWeek.setDate(diff);

    const year = startOfWeek.getFullYear();
    const weekNumber = Math.ceil(
      ((startOfWeek.getTime() - new Date(year, 0, 1).getTime()) / 86400000 + 1) / 7
    );

    return `${year}-W${String(weekNumber).padStart(2, '0')}`;
  }

  subscribeToDashboardStats(callback: (data: Record<string, unknown> | null) => void): () => void {
    return this.firestore.subscribeToDocument(
      this.COLLECTION,
      this.DASHBOARD_DOC_ID,
      (doc) => {
        if (!doc) {
          callback(null);
          return;
        }
        const { id: _ignored, ...rest } = doc as Record<string, unknown>;
        callback(rest);
      },
    );
  }

  subscribeToStats(callback: (stats: Stats) => void): () => void {
    return this.firestore.subscribeToDocument(
      this.COLLECTION,
      this.STATS_DOC_ID,
      (doc) => {
        const stats = doc ? Stats.fromFirestore(doc) : new Stats();
        callback(stats);
      }
    );
  }
}